//import { Box, Typography } from '@mui/material';
import { QuestionnaireResponse } from 'fhir/r4b';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { flattenQuestionnaireAnswers } from 'utils';
import { InformationCard } from '../../telemed/components/patient-visit-details/InformationCard';

interface PatientSubmittedInformationProps {
  questionnaireResponse?: QuestionnaireResponse;
}

// These are linkIds that are already prominently displayed in other cards.
const EXCLUDED_LINK_IDS = [
  'signature',
  'full-name',
  'consent-form-signer-relationship',
  'hipaa-acknowledgement',
  'consent-to-treat',
  'patient-email',
  'patient-number',
  'patient-street-address',
  'patient-street-address-2',
  'patient-city',
  'patient-state',
  'patient-zip',
  'insurance-carrier',
  'insurance-carrier-2',
  'policy-holder-date-of-birth',
  'policy-holder-date-of-birth-2',
];

export const PatientSubmittedInformation: FC<PatientSubmittedInformationProps> = ({ questionnaireResponse }) => {
  const { t } = useTranslation();

  const answers = useMemo(() => {
    if (!questionnaireResponse || !questionnaireResponse.item) return [];

    const flattened = flattenQuestionnaireAnswers(questionnaireResponse.item);

    const relevantAnswers = flattened.filter(
      (item) =>
        item.linkId && item.text && !EXCLUDED_LINK_IDS.includes(item.linkId) && item.answer && item.answer.length > 0
    );

    return relevantAnswers.map((item) => {
      const allAnswers = (item.answer as any[])
        ?.map((ans) => {
          if (ans.valueString !== undefined) return ans.valueString;
          if (ans.valueBoolean !== undefined) return ans.valueBoolean ? t('Yes') : t('No');
          if (ans.valueDate !== undefined) return ans.valueDate;
          if (ans.valueInteger !== undefined) return ans.valueInteger.toString();
          if (ans.valueDecimal !== undefined) return ans.valueDecimal.toString();
          if (ans.valueCoding !== undefined) return ans.valueCoding.display || ans.valueCoding.code || '';
          // Custom valueArray for list-like questions (e.g. allergies, surgeries)
          if (ans.valueArray !== undefined && Array.isArray(ans.valueArray)) {
            return ans.valueArray
              .map((obj: any) => Object.values(obj).filter(Boolean).join(': '))
              .filter(Boolean)
              .join(', ');
          }
          return '';
        })
        .filter(Boolean);

      return {
        label: item.text || '',
        value: allAnswers?.length ? allAnswers.join(', ') : '-',
      };
    });
  }, [questionnaireResponse, t]);

  if (!answers || answers.length === 0) {
    return null;
  }

  return (
    <InformationCard title={t('patientSubmittedInformation.title', 'Patient Submitted Information')} fields={answers} />
  );
};
